import {combineLatest, Observable, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngxs/store';
import {EChartsCoreOption} from 'echarts/core';
import {Submission} from '../../../symbols/submission.symbols';
import {ProfileState} from '../../../../shared/microservices/profile/profile.state';
import {getSexCode} from '../../../../shared/functions/utils';
import {SubmissionState} from '../../../../shared/microservices/submission/submission.state';
import {GradientBreakpoint} from '../../../../shared/functions/charts';
import { NgTemplateOutlet, NgStyle } from '@angular/common';
import { SimpleExpansionPanelComponent } from '../../../components/simple-expansion-panel/simple-expansion-panel.component';
import { NgxEchartsDirective } from 'ngx-echarts';
import { RtLoadingComponent } from '../../../../rt-loading/rt-loading/rt-loading.component';


@Component({
    selector: 'app-brain-age',
    templateUrl: './brain-age.component.html',
    styleUrls: ['./brain-age.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, RtLoadingComponent, SimpleExpansionPanelComponent, NgStyle, NgxEchartsDirective]
})
export class BrainAgeComponent implements OnInit, OnDestroy {
  public readonly selectedSubmission$ = this.store.select(SubmissionState.selectedSubmission);
  public readonly brainAge$ = this.store.select(SubmissionState.brainAge);
  public readonly person$ = this.store.select(ProfileState.person);

  scanAge$: Observable<number>;
  deducedSex$: Observable<number>;
  ageOrSexMissing$: Observable<boolean>;

  private subscription = new Subscription();

  private _computedBrainAge: number;
  get computedBrainAge(): number {
    return this._computedBrainAge;
  }

  set computedBrainAge(brainAge: number) {
    if (typeof this.personAge === 'number') {
      this.ageDifference = brainAge - this.personAge;
    }
    this._computedBrainAge = brainAge;
    this.cd.detectChanges();
  }

  private _personAge: number;
  get personAge(): number {
    return this._personAge;
  }

  set personAge(personAge: number) {
    if (typeof this.computedBrainAge === 'number') {
      this.ageDifference = personAge - this.computedBrainAge;
    }
    this._personAge = personAge;
    this.cd.detectChanges();
  }

  ageDifference = 0;
  /** Relation between age difference and arrow rotate degrees: Degrees = (difference * ratio) */
  readonly scaleRatio = 3;

  selectedSubmission: Submission = null;

  volumeChartConfig: EChartsCoreOption = null;

  getVolumeChartConfig(userAge: [number, number][] = []): EChartsCoreOption {
    const charts = userAge.map(ages => this.getChartPointData(ages[0], ages[1]));
    return {
      xAxis: {
        type: 'value',
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        splitLine: {
          show: false
        },
        axisLabel: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      series: [
        {
          data: [[0, 0], [100, 100]],
          symbol: 'none',
          type: 'line',
        },
        ...charts,
      ],
      color: ['#000000', '#2E4FD7', '#2E4FD7', '#2E4FD7', '#2E4FD7', '#2E4FD7', '#2E4FD7', '#2E4FD7', '#2E4FD7', '#2E4FD7', '#2E4FD7'],
    };
  }


  getChartPointData(personAge: number, brainAge: number) {
    return {
      data: [[personAge, brainAge]],
      symbol: 'circle',
      type: 'line' as 'line', // echart has no exported member like `SeriesOption$1` that used for type compilation
      symbolSize: 8,
    };
  }

  get computedAgePercentile() {
    if (!this.computedBrainAge || !this.personAge) {
      return;
    }

    const min = this.personAge - 25;
    const max = this.personAge + 25;

    let percentile;

    if (this.computedBrainAge <= min) {
      percentile = 0;
    } else if (this.computedBrainAge >= max) {
      percentile = 100;
    } else {
      percentile = (this.computedBrainAge - min) / (max - min) * 100;
    }

    return percentile;
  }

  brainAgeGradientBreakpoints: GradientBreakpoint[] = [
    {
      position: 0,
      color: '6927C4'
    },
    {
      position: 36.11,
      color: '48BD9C'
    },
    {
      position: 63.63,
      color: '48BD9C'
    },
    {
      position: 94.24,
      color: '6927C4'
    }
  ];

  constructor(
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
  ) {
  }

  ngOnInit(): void {

    this.volumeChartConfig = this.getVolumeChartConfig([[42, 57]]);

    this.scanAge$ = this.selectedSubmission$.pipe(
      map((selectedSubmission) => {
        return selectedSubmission ? selectedSubmission.age_at_scan : null;
      })
    );

    this.deducedSex$ = combineLatest([this.person$, this.selectedSubmission$]).pipe(
      map(([person, selectedSubmission]) => {
        return selectedSubmission.is_public ? getSexCode(selectedSubmission) : (person && person.sex);
      })
    );

    this.ageOrSexMissing$ = combineLatest([this.scanAge$, this.deducedSex$]).pipe(
      map(([age, sex]) => {
        if (this.selectedSubmission && this.selectedSubmission.is_shared) {
          return (!age && age !== 0);
        } else {
          return (!age && age !== 0) || !sex;
        }
      })
    );

    this.subscription.add(
      this.selectedSubmission$.subscribe(selectedSubmission => {
        this.selectedSubmission = selectedSubmission;
        this.cd.detectChanges();
      })
    );

    this.subscription.add(
      this.scanAge$.subscribe(age => {
        this.personAge = age;
        this.cd.detectChanges();
      })
    );

    this.subscription.add(
      this.brainAge$.subscribe(computedAge => {
        this.computedBrainAge = computedAge;
        this.cd.detectChanges();
      })
    );
  }

  back() {
    this.router.navigate(['../'], {relativeTo: this.route});
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
